import { useState } from "react";
import type { Authorization } from "../../api/types";
import { t } from "../../i18n";
import { AllergensTab } from "./AllergensTab";
import { FolderTab } from "./FolderTab";
import { GroupsTab } from "./GroupsTab";
import { ProductsTab } from "./ProductsTab";
import { useMenuEditor } from "./useMenuEditor";

type Tab = "products" | "groups" | "allergens" | "folder";

interface Props {
  /** The owner's Beheer code: only the owner edits the menu and the prices (besluiten 28/09). */
  authorization: Authorization;
}

/**
 * Beheer › Menu & prijzen, as in the prototype: three tabs (Producten, Keuzegroepen, Allergenen), and Folder: a folder
 * of the menu to print (ADR 0050). Every change is a new menu version, active at once on every device.
 */
export function MenuScreen({ authorization }: Props) {
  const editor = useMenuEditor(authorization);
  const [tab, setTab] = useState<Tab>("products");
  /** A product to open in the editor (from the Allergenen tab: "Bekijken"). */
  const [focus, setFocus] = useState<{ productId: string; allergens: boolean } | null>(null);

  return (
    <div className="panel admin-section menu-admin">
      <h2>{t.menuAdmin.title}</h2>
      <div className="menutop">
        <div className="seg" role="tablist" aria-label={t.menuAdmin.title}>
          {(["products", "groups", "allergens", "folder"] as const).map((id) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              aria-pressed={tab === id}
              onClick={() => {
                setTab(id);
              }}
            >
              {t.menuAdmin.tabs[id]}
            </button>
          ))}
        </div>
        <span className="note">{t.menuAdmin.note}</span>
      </div>
      {tab === "products" && <ProductsTab editor={editor} focus={focus} />}
      {tab === "groups" && <GroupsTab editor={editor} />}
      {tab === "folder" && <FolderTab authorization={authorization} />}
      {tab === "allergens" && (
        <AllergensTab
          editor={editor}
          authorization={authorization}
          onOpen={(productId) => {
            setFocus({ productId, allergens: true });
            setTab("products");
          }}
        />
      )}
    </div>
  );
}
